import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Stethoscope, Award } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function BecomeProfessional({ user, onSave, onClose }) {
  const [formData, setFormData] = useState({
    professional_type: user?.professional_type || '',
    professional_license: user?.professional_license || '',
    professional_specialty: user?.professional_specialty || '',
    professional_bio: user?.professional_bio || ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSave({
        ...formData,
        is_professional: true,
        professional_verified: false
      });
      toast.success('Solicitud enviada. Un administrador verificará tus credenciales.');
      onClose();
    } catch (error) {
      toast.error('Error al enviar solicitud');
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg overflow-hidden"
        >
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Convertirme en Profesional</h2>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <div className="flex items-start gap-2">
                <Award className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Beneficios para profesionales</p>
                  <ul className="text-xs text-blue-700 mt-2 space-y-1 list-disc list-inside">
                    <li>Badge verificado en tu perfil</li>
                    <li>Mayor visibilidad en la comunidad</li>
                    <li>Posibilidad de publicar contenido especializado</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de profesional *
              </label>
              <Select 
                value={formData.professional_type}
                onValueChange={(value) => setFormData({...formData, professional_type: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona tu profesión" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nutricionista">Nutricionista</SelectItem>
                  <SelectItem value="medico">Médico</SelectItem>
                  <SelectItem value="enfermero">Enfermero</SelectItem>
                  <SelectItem value="farmaceutico">Farmacéutico</SelectItem>
                  <SelectItem value="otro">Otro profesional de salud</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Número de matrícula/licencia *
              </label>
              <Input
                value={formData.professional_license}
                onChange={(e) => setFormData({...formData, professional_license: e.target.value})}
                placeholder="MP 12345"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Especialidad (opcional)
              </label>
              <Input
                value={formData.professional_specialty}
                onChange={(e) => setFormData({...formData, professional_specialty: e.target.value})}
                placeholder="Ej: Nutrición deportiva"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Biografía profesional (opcional)
              </label>
              <Textarea
                value={formData.professional_bio}
                onChange={(e) => setFormData({...formData, professional_bio: e.target.value})}
                placeholder="Breve descripción de tu experiencia y enfoque profesional"
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={!formData.professional_type || !formData.professional_license}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Enviar solicitud
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}